import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { BaseComponent } from '@ups/xplat/core';
import { takeUntil } from 'rxjs/operators';
import {
  IDynamicModel,
  IDynamicModelGroup,
} from '../../models/dynamic.interface';
import { DynamicEventBusTypes } from '../../utils/dynamic-event-bus-types';
import { emitScopedFormReset } from '../../utils/dynamic-form-reset';
import { DynamicRenderService } from '../../services/dynamic-render.service';

@Directive()
export class DynamicContainerBaseComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  form: UntypedFormGroup;
  fb = inject(UntypedFormBuilder);
  dynamicService = inject(DynamicRenderService);
  @Input() dynamicGroup: IDynamicModelGroup;
  @Input() isProperties: boolean;
  @Input() showWhatsNew: boolean;
  /** WI 32220: the active form this container took over (a page under a dialog). */
  private _coveredActiveForm: UntypedFormGroup | undefined;

  get dynamicItems(): IDynamicModel[] {
    if (this.dynamicGroup && this.dynamicGroup.controls) {
      return this.dynamicGroup.controls;
    } else {
      return [];
    }
  }

  ngOnInit() {
    this.dynamicService.eventBus
      .observe(DynamicEventBusTypes.dynamicFormTotalReset)
      .pipe(takeUntil(this.destroy$))
      .subscribe(this._resetActiveForm.bind(this));
    if (this.isProperties) {
      this.dynamicService.activeFormProperties = this.form = this.fb.group({});
    } else {
      this._coveredActiveForm = this.dynamicService.activeForm;
      this._resetActiveForm();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    // clear any active attachments
    this.dynamicService.activeAttachments = {};
    // WI 32220: resets now reach only activeForm, so hand it back to the form this
    // container covered, or a Discard on the page under a closed dialog resets nothing.
    // Only while this container still owns it, never over a newer container's form.
    if (
      this._coveredActiveForm &&
      this.dynamicService.activeForm === this.form
    ) {
      this.dynamicService.activeForm = this._coveredActiveForm;
    }
  }

  private _resetActiveForm() {
    this.dynamicService.clearActiveForm(true);
    // WI 32220: reset only THIS container's outgoing form. Its controls survive a total
    // reset still bound to it until change detection rebinds them; on first init there
    // is none, so another container's still-open form (a page under a dialog) is untouched.
    emitScopedFormReset(this.dynamicService.eventBus, this.form);
    this.dynamicService.activeForm = this.form = this.fb.group({});
    this.dynamicService.eventBus.emit(DynamicEventBusTypes.dynamicFormReady);
  }
}
